'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { elowenClient } from '../../lib/elowenClient';
import { useOperationFailure, useToast } from '../../components/ui/Toast';
import { holdsAnswer, releasing } from '../../lib/saveFeedback';
import { useTranslation } from '../../lib/i18n';
import type { SaveStatus } from '../../lib/useAutoSaveStatus';

interface WriteState {
  scope: string;
  applied: string | null;
  status: SaveStatus;
  retryLevel: string | null;
}

/** The single live reasoning write for /reasoning, the keyboard cycle, the chat bar and both model catalog hosts:
 * the chat controller owns ONE instance of it, so a hold set by one entry point stops every other.
 * An optional switch runs first; a refused switch never writes the account default.
 * With no switch, POST /brain/think applies to the active conversation immediately.
 * Success refreshes the Account CLI setting and the session status; failure rolls back
 * the optimistic level and displays the same toast on every surface.
 *
 * The displayed write state belongs to one conversation and one model: it resets when either changes, and a
 * write that settles after that is dropped, so one conversation's level never shows in another. `applied` is
 * only an optimistic value: once the write is confirmed and the session status has been refetched it is cleared,
 * so the server's value is what the UI reads afterwards. What a running or unknown write forbids is keyed by the
 * CONVERSATION alone (it is that conversation's level, whichever model a switch moved it to), so it survives the
 * model change a switch causes. A write whose answer was lost is held until the daemon's own level has been read
 * back, with or without a status query mounted. */
export function useReasoningLevel(session: string | null, model = '') {
  const { dismissKey } = useToast();
  const reportFailure = useOperationFailure();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const scope = `${session ?? ''}\u0000${model}`;
  const [state, setState] = useState<WriteState>({ scope, applied: null, status: 'idle', retryLevel: null });
  // What a running or lost write forbids, per conversation: a write finishing for one conversation clears only
  // its own entry, never the entry another conversation's pending write set meanwhile.
  const [guards, setGuards] = useState<ReadonlyMap<string, 'saving' | 'unconfirmed'>>(new Map());
  const guardsRef = useRef(guards);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  // Adjusting state while rendering is React's way to reset on a prop change without a stale frame.
  const current: WriteState = state.scope === scope ? state : { scope, applied: null, status: 'idle', retryLevel: null };
  if (state.scope !== scope) setState(current);
  const guarded = guards.get(session ?? '') ?? null;
  const patch = (forScope: string, next: Partial<WriteState>): void => {
    if (scopeRef.current !== forScope) return;
    setState((prev) => (prev.scope === forScope ? { ...prev, ...next } : prev));
  };
  const setGuardFor = (forSession: string | null, kind: 'saving' | 'unconfirmed' | null): void => {
    const next = new Map(guardsRef.current);
    if (kind === null) next.delete(forSession ?? ''); else next.set(forSession ?? '', kind);
    guardsRef.current = next;
    setGuards(next);
  };

  const apply = async (level: string, shown: string, switchModel?: () => Promise<boolean>): Promise<void> => {
    // The same level writes nothing, and a write running or whose answer was lost is not stacked on or repeated.
    if (!level || (level === shown && !switchModel)) return;
    if (guardsRef.current.has(session ?? '')) return;
    const writeScope = scope;
    const writeSession = session;
    const key = `reasoning:${session ?? ''}`;
    dismissKey(key);
    setGuardFor(writeSession, 'saving');
    patch(writeScope, { retryLevel: level, status: 'saving' });
    try {
      if (switchModel) {
        if (!await switchModel()) {
          patch(writeScope, { status: 'error' });
          setGuardFor(writeSession, null);
          return;
        }
        // A successful switch respawns PI. Refresh the previous model's status even
        // if the subsequent reasoning write fails and we have to show the new model.
        void queryClient.invalidateQueries({ queryKey: ['brain-session-status', session] });
      }
      patch(writeScope, { applied: level });
      const result = await elowenClient.brainThink(level, session ?? undefined);
      patch(writeScope, { applied: result.thinkingLevel, status: 'saved' });
      void queryClient.invalidateQueries({ queryKey: ['my-cli-settings'] });
      await queryClient.invalidateQueries({ queryKey: ['brain-session-status', session] });
      patch(writeScope, { applied: null });
      setGuardFor(writeSession, null);
    } catch (error) {
      const held = holdsAnswer(error);
      patch(writeScope, { applied: null, status: held ? 'unconfirmed' : 'error' });
      setGuardFor(writeSession, held ? 'unconfirmed' : null);
      reportFailure(key, error, {
        message: t.common.saveFailed,
        // The daemon's level is fetched, not merely invalidated: with no status query mounted (the keyboard cycle)
        // an invalidation reads nothing, and the fetch adopts the answer into the cache for every reader.
        reload: releasing(async () => {
          await queryClient.fetchQuery({
            queryKey: ['brain-session-status', writeSession],
            queryFn: () => elowenClient.brainStatus(writeSession ?? undefined),
            staleTime: 0,
          });
        }, () => { patch(writeScope, { applied: null, status: 'idle' }); setGuardFor(writeSession, null); }),
      });
    }
  };

  // The chat controller hands this object to every consumer through its stable actions value, so it keeps one
  // identity until a field a reader shows changes: a streamed token re-renders the provider and must not.
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const stableApply = useCallback((...args: Parameters<typeof apply>) => applyRef.current(...args), []);
  const status = guarded ?? current.status;
  return useMemo(
    () => ({ applied: current.applied, status, retryLevel: current.retryLevel, apply: stableApply }),
    [current.applied, status, current.retryLevel, stableApply],
  );
}
